import { maybeWebContext } from "../search";
// chat/brain.ts — the chat brain for Oriora Plant Diary, SERVER-FIRST (the R8 graduation,
// mirrors study_companion). askBrain now sends the message + this app's declared action
// catalog to the generic fleet chat brain (/api/apps/oriora_plant_diary/chat) — ONE billed
// call returns ONE registered action + a reply; the app executes it locally so data stays
// on-device. The regex intent-router below is the FREE FALLBACK (offline / signed-out / out
// of funds) — chat never goes dead. Original prototype notes:
//   • "add plant …"                       → local intent  → add_plant     (store.createPlant)
//   • "water/feed/repot/prune/note <p>"    → local intent  → log_care      (store.createEvent)
//   • "rename/set/change/move <p> …"       → local intent  → edit_plant    (store.updatePlant)
//   • "delete/remove <p>"                  → resolve id     → delete_plant  (UI confirms)
//   • "show/find/list/search … plants"     → local search   → filter        (store.listPlants)
//   • "recap / this week / story of <p>"   → existing brain → recap          (narrateThisWeek/Story)
//   • anything else                        → none + a short clarifying nudge
//
// HUMAN/PET SAFETY FENCE (HARD, pre-LLM): the ONE thing this app won't touch is a question
// about a person's or an animal's health — "is it toxic to my cat", "is this mould dangerous".
// crossesHumanSafetyFence() runs on the raw message BEFORE the server routing call is made, so
// it's refused for FREE and never reaches the billed call. (Routing through an LLM means the raw
// message reaches a model every turn, so the fence lives here too — defence in depth alongside
// the server prompt.)
//
// Plant questions themselves are NOT fenced (rescoped 2026-07-09): a plant is not a person.
// "Why is it yellowing" gets an honest, SOURCED answer from the brain — never a prescription
// stated as fact. See BRAIN-PATTERNS Pattern 7.

import { store, kindLabel, type Plant, type CareKind, type PlantPatch } from "../store";
import { BrainError, crossesHumanSafetyFence, REFUSAL_MESSAGE, brainCall } from "../brain";
import type { ChatAction, PlantRow } from "./actions";
import { findNamedMatches, notFoundMessage } from "./fuzzy";
import { libraryFind, librarySummary } from "../library";

export interface ChatTurn {
  role: "user" | "assistant";
  content: string;
  rows?: PlantRow[]; // assistant turns may carry search matches to render inline
}
export interface BrainResponse {
  reply: string;      // conversational preface; executeAction supplies the result line
  action: ChatAction; // the first action — kept so every existing caller still works
  /** ALL actions, in order (Pattern 5 Tier A: "water the fern and the monstera" does BOTH).
   *  Absent/one-length for a normal single-action turn. */
  actions?: ChatAction[];
}

const HELP =
  "I can add plants, log care, edit, search, delete, and recap — just tell me. Try:\n" +
  '• "add plant Monstera species Ficus in living room"\n' +
  '• "water Monstera" · "fed the fern - soil was dry" · "note Monstera: new leaf"\n' +
  '• "milestone Monstera: first flower" · "the monstera is 42cm now" · "it has 11 leaves"\n' +
  '• "rename Monstera to Big Monstera" · "move the fern to the kitchen"\n' +
  '• "show plants in the kitchen" · "find ficus"\n' +
  '• "recap this week" · "story of Monstera"\n' +
  '• "delete Monstera"';

// ── Care verbs → CareKind. Each matches only at the START of the message. ────────
const CARE_VERBS: { re: RegExp; kind: CareKind }[] = [
  { re: /^water(?:ed|ing)?\b/i, kind: "water" },
  { re: /^(?:feed|fed|feeding|fertili[sz]e[ds]?|fertili[sz]ing)\b/i, kind: "feed" },
  { re: /^repot(?:ted|ting)?\b/i, kind: "repot" },
  { re: /^(?:prune[ds]?|pruning|trim(?:med|ming)?)\b/i, kind: "prune" },
  { re: /^note\b/i, kind: "note" },
  { re: /^(?:milestone|flowered|bloomed|propagated)\b/i, kind: "milestone" },
];

const clean = (s: string) => s.replace(/[.?!]+$/, "").trim();

/**
 * Resolve a plant the way a person would (BRAIN-PATTERNS Pattern 6): exact name → name/species
 * substring → fuzzy, misspelling-tolerant match. Exact-substring behaviour is unchanged when it
 * works; fuzzy only runs after it fails, and only matches when EVERY query token has a near-token,
 * so it can't invent a match out of thin air. All name→id paths (log, edit, delete, growth,
 * recap) go through here, which is what keeps "monstera delicioza" from looking like a broken app.
 */
export async function findByName(term: string): Promise<Plant[]> {
  const q = clean(term).toLowerCase();
  if (!q) return [];
  const all = await store.listPlants();
  const exact = all.filter((p) => p.name.toLowerCase() === q);
  if (exact.length) return exact;
  // Species is plant-specific (curio's fuzzy.ts only knows `name`), so keep the substring pass here.
  const subs = all.filter((p) => p.name.toLowerCase().includes(q) || p.species.toLowerCase().includes(q));
  if (subs.length) return subs;
  return findNamedMatches(all, term);
}

/** "I don't see X — did you mean Y?" instead of a dead end. */
async function noMatch(term: string): Promise<BrainResponse> {
  return none(notFoundMessage(await store.listPlants(), term));
}

function pickList(rows: Plant[]): string {
  return rows.slice(0, 5).map((p) => `• ${p.name}${p.species ? ` (${p.species})` : ""}`).join("\n");
}

const none = (message: string): BrainResponse => ({ reply: "", action: { kind: "none", message } });

export async function askBrain(history: ChatTurn[], onChunk?: (t: string) => void): Promise<BrainResponse> {
  const last = [...history].reverse().find((t) => t.role === "user");
  const text = (last?.content ?? "").trim();
  if (!text) return routeMessage(text);
  // PLANT FENCE — pre-LLM, free. A diagnosis/treatment/health-judgement ask is refused
  // deterministically before it can reach the (billed) routing call.
  if (crossesHumanSafetyFence(text)) return none(REFUSAL_MESSAGE);
  try {
    return await serverChat(history, text, onChunk);
  } catch (e) {
    // A money/auth/key refusal must be SAID, not masked: the local fallback answering a
    // funds-blocked question as if it were a search reads as a broken app (found live 2026-08-27).
    if (e instanceof BrainError && (e.code === "insufficient_funds" || e.code === "unauthenticated" || e.code === "managed_ai_blocked")) {
      return none(e.message);
    }
    // Offline / transient server trouble → the free local intent-router below
    // still answers; chat never goes dead.
    return routeMessage(text);
  }
}

// ---- SERVER CHAT-BRAIN (the graduated path) --------------------------------
// The app declares WHAT it can do; the fleet chat brain decides WHICH action the
// message means; this file resolves references (plant name → id) locally, because
// the data lives here.

// Standing instructions (fleet standard): the customer shapes the app through chat; stored
// on-device, injected into every brain call once this app runs the server-first chat.
const INSTRUCTIONS_KEY = "oriora.plant-diary.instructions";
export const getInstructions = (): string => { try { return localStorage.getItem(INSTRUCTIONS_KEY) ?? ""; } catch { return ""; } };
export const addInstruction = (line: string): void => { try { const cur = getInstructions(); localStorage.setItem(INSTRUCTIONS_KEY, cur ? cur + "\n- " + line : "- " + line); } catch { /* ignore */ } };

// The catalog the brain sees each turn. Keep descriptions SHORT and distinctive: at ~5KB the
// model started dropping the strict-JSON contract on multi-item turns (1 in 3 collapsed to the
// "I didn't get that quite right" fallback). Every word here is prompt on every single message.
export const CHAT_ACTIONS = [
  { name: "add_plant", description: "Add a new plant.", params: { name: "plant name", species: "optional species", location: "optional location", potted_on: "optional YYYY-MM-DD" } },
  { name: "log_care", description: "Log care for one plant: water, feed, repot, prune, note, or milestone (a moment worth remembering — new leaf, flowered, took a cutting).", params: { plant: "plant name as written", care: "REQUIRED: water|feed|repot|prune|note|milestone|photo", note: "optional; for a milestone, what happened", done_on: "optional YYYY-MM-DD, defaults today" } },
  { name: "log_growth", description: "Record a plant's height in cm and/or leaf count. 'the monstera is 42cm now', 'it has 11 leaves'.", params: { plant: "plant name", height_cm: "optional number", leaf_count: "optional whole number", measured_on: "optional YYYY-MM-DD", note: "optional" } },
  { name: "edit_plant", description: "Change a plant's name, species, location or potted date.", params: { plant: "current name", new_name: "only if renaming", species: "only if changing", location: "only if changing", potted_on: "only if changing" } },
  { name: "delete_plant", description: "Delete a plant and its whole care log (user confirms first).", params: { plant: "plant name" } },
  { name: "delete_event", description: "Delete ONE timeline entry (a watering, feeding, note, milestone). Omit done_on to mean the most recent (user confirms first).", params: { plant: "plant name", care: "optional water|feed|repot|prune|note|milestone|photo", done_on: "optional YYYY-MM-DD" } },
  { name: "delete_growth", description: "Delete ONE growth measurement. Omit measured_on to mean the most recent (user confirms first).", params: { plant: "plant name", measured_on: "optional YYYY-MM-DD" } },
  { name: "save_to_cloud", description: "Save a note, or THIS CONVERSATION, to the customer's own GitHub/Drive.", params: { subject: "REQUIRED: conversation|note", name: "short title", content: "the note text; required when subject=note", provider: "optional github|gdrive if they name one" } },
  { name: "filter", description: "Search or list plants by name, species or location.", params: { text: "search text" } },
  { name: "recap", description: "Narrate the care rhythm — the whole collection's week, or ONE plant's story if a plant is named.", params: { plant: "optional plant name" } },
  { name: "backup_save", description: "Back up the whole collection to the customer's own GitHub/Drive. Works for ANY size — if the backup is too large for the standard path (photos), it saves straight from this device to their GitHub when that's logged in (backup menu), or the reply tells them the one-time login to enable it.", params: {} },
  { name: "backup_status", description: "Tell the customer WHERE their data is backed up: which cloud is connected in their Oriora account, which one new backups write to, and when the last backup was. Use for 'where is my data / is my stuff backed up / which cloud do you use'.", params: {} },
  { name: "backup_restore", description: "Restore from their cloud backup. REPLACES current data (user confirms first). If they NAME a source ('restore from Drive'), pass provider.", params: { provider: "optional github|gdrive when the customer names which cloud to restore from" } },
  { name: "save_local_copy", description: "Save a copy of their data to a file on this device (OS picker).", params: {} },
  { name: "import_file", description: "Import/restore their data from a FILE they pick on this device. REPLACES current data (user confirms first).", params: {} },
  { name: "remember", description: "Save a standing instruction for how this customer wants the app to behave from now on.", params: { instruction: "the preference, one sentence" } },
  // ── ARTIFACT TOOLS — the customer's own library (device-only). Whatever the AI
  // makes for them gets KEPT here instead of vanishing into the chat. Fleet-wide
  // capability, identical in every Oriora app.
  { name: "library_save", description: "Save a plan, summary, guide or note into the customer's ON-DEVICE library. Use whenever you produce something worth keeping, or they ask you to save/remember/write something down. When they ask you to RESEARCH something and make them a plan: write the FULL text in \`content\`, give it a clear title, and put a short summary in your reply.", params: { title: "short title", item_kind: "lesson | plan | note", content: "the FULL text to save — write it out completely", folder: "optional folder name" } },
  { name: "library_list", description: "List what the customer has saved in their library ('what have I saved', 'show my plans', 'what's in my library / in folder X').", params: { folder: "optional folder to filter by" } },
  { name: "library_remove", description: "Remove ONE library item by its title (the app asks the customer to confirm first). LIBRARY items only — the app's own entries have their own delete action.", params: { title: "which item (its title, from LIBRARY in the context)" } },
  { name: "library_move", description: "Move a library item into a folder ('move the summer plan into Plans').", params: { title: "which item", folder: "target folder name" } },
  { name: "library_merge", description: "Merge two or more library items into one (the app asks the customer to confirm first).", params: { titles: "the titles to merge, separated by |", new_title: "title for the merged item" } },
];

// The shared brain answers with an ARRAY (one entry per thing the user asked for) and also
// mirrors the first into `action`/`params` for older clients. We read the array.
interface ServerAct { name?: string; action?: string; params?: Record<string, unknown> }
interface ServerChat { actions?: ServerAct[]; action?: string; params?: Record<string, unknown>; reply?: string }

// The app's declared domain + its own hard limits. The shared brain reads this to decide what is
// in-scope; without `assist` it would fall into the strict tracking branch and refuse every plant
// question, including "how's it doing" (BRAIN-PATTERNS Pattern 5 Tier B).
//
// ⚠️ The server TRUNCATES scope at 400 chars — so the HARD LIMIT goes FIRST. A longer string got
// sliced mid-clause, leaving "...whether mould is dangerous to bre" with the refusal instruction
// cut off entirely. Keep this under 400 and lead with the safety rule. Verified by test below.
const SCOPE =
  "HARD LIMIT: refuse any question about a PERSON's or ANIMAL's health or safety (toxicity, " +
  "edibility, a pet ate the plant, is mould dangerous) — point them to a vet or doctor. " +
  "Otherwise: a plant-care diary — the customer's plants, care log, photos, growth. General " +
  "plant care is allowed as fallible AI reasoning, never a prescription, never a made-up source.";

async function serverChat(history: ChatTurn[], text: string, onChunk?: (t: string) => void): Promise<BrainResponse> {
  const plants = await store.listPlants();
  const list = plants.slice(0, 40).map((p) => p.name + (p.species ? ` (${p.species})` : "")).join(", ");
  const _notes = getInstructions();
  const context = (`LIBRARY (their saved plans/notes): ${librarySummary()}. Plants: ${list || "(none yet)"}.`) + (_notes ? "\nCustomer's standing instructions (apply when filling params): " + _notes : "");
  const recent = history.slice(-21, -1).map((t) => ({ role: t.role, content: t.content }));
  const r = await brainCall<ServerChat>("chat", {
    message: text + (await maybeWebContext(text)), actions: CHAT_ACTIONS, context, history: recent, scope: SCOPE, assist: true,
  }, onChunk);

  const acts = Array.isArray(r.actions) ? r.actions.filter((a) => a && (a.name ?? a.action)) : [];
  if (acts.length <= 1) {
    const only = acts[0];
    return toAction({ action: String(only?.name ?? only?.action ?? r.action ?? ""), params: only?.params ?? r.params, reply: r.reply });
  }

  // Multi-action turn. Resolve each independently — one unresolvable item (a name we can't find)
  // must NOT silently drop the others, so its clarifying message rides along as a `none` action.
  const resolved: ChatAction[] = [];
  for (const a of acts) {
    const one = await toAction({ action: String(a.name ?? a.action ?? ""), params: a.params, reply: "" });
    resolved.push(one.action);
  }
  return { reply: String(r.reply ?? ""), action: resolved[0], actions: resolved };
}

const asStr = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim() : undefined);
/** The brain may hand a number back as a string ("42", "42cm") — take the number, ignore the rest. */
const asNum = (v: unknown): number | undefined => {
  if (typeof v === "number") return Number.isFinite(v) ? v : undefined;
  if (typeof v === "string") {
    const m = v.match(/-?\d+(?:\.\d+)?/);
    if (m) { const n = Number(m[0]); return Number.isFinite(n) ? n : undefined; }
  }
  return undefined;
};
const asDate = (v: unknown): string | undefined => {
  const m = asStr(v)?.match(/\b(\d{4}-\d{2}-\d{2})\b/);
  return m ? m[1] : undefined;
};
const CARE_KINDS_SET = new Set<CareKind>(["water", "feed", "repot", "prune", "note", "milestone", "photo"]);

export async function toAction(r: ServerChat): Promise<BrainResponse> {
  const p = r.params ?? {};
  const say = asStr(r.reply) ?? "";

  // ── ARTIFACT TOOLS (the library) — handled EARLY, above any app-specific
  // precondition (no vehicle / no plant / nothing selected). The library belongs
  // to the customer, not to a currently-active record. (Garage shipped this bug:
  // its no-vehicle guard silently swallowed every library action, 2026-08-13.)
  if (r.action === "library_save") {
    const _content = asStr(p.content);
    if (!_content) return none("Tell me what to save — or ask me to write it first and I'll keep it.");
    const _rawKind = (asStr(p.item_kind) ?? "note").toLowerCase();
    const _itemKind = _rawKind === "lesson" || _rawKind === "plan" ? _rawKind : "note";
    return { reply: say, action: { kind: "library_save", title: asStr(p.title) ?? "Untitled", itemKind: _itemKind, content: _content, folder: asStr(p.folder) } };
  }
  if (r.action === "library_list") return { reply: say, action: { kind: "library_list", folder: asStr(p.folder) } };
  if (r.action === "library_remove") {
    const _r = resolveLibraryItem(asStr(p.title));
    if (!_r.item) return none(_r.problem!);
    return { reply: say, action: { kind: "library_remove", id: _r.item.id, label: `"${_r.item.title}"` } };
  }
  if (r.action === "library_move") {
    const _folder = asStr(p.folder);
    if (!_folder) return none("Which folder should it go to?");
    const _r2 = resolveLibraryItem(asStr(p.title));
    if (!_r2.item) return none(_r2.problem!);
    return { reply: say, action: { kind: "library_move", id: _r2.item.id, folder: _folder, label: `"${_r2.item.title}"` } };
  }
  if (r.action === "library_merge") {
    const _titles = (asStr(p.titles) ?? "").split("|").map((s) => s.trim()).filter(Boolean);
    if (_titles.length < 2) return none("Tell me which two (or more) items to merge, by title.");
    const _ids: string[] = [];
    for (const _t of _titles) {
      const _r3 = resolveLibraryItem(_t);
      if (!_r3.item) return none(_r3.problem!);
      _ids.push(_r3.item.id);
    }
    return { reply: say, action: { kind: "library_merge", ids: _ids, title: asStr(p.new_title) ?? _titles.join(" + "), label: _titles.map((t) => `"${t}"`).join(" + ") } };
  }
  switch (r.action) {
    case "add_plant": {
      const name = asStr(p.name);
      if (!name) return none(say || 'What should the plant be called? Try "add plant Monstera".');
      return { reply: say, action: { kind: "add_plant", name, species: asStr(p.species), location: asStr(p.location), potted_on: asDate(p.potted_on) } };
    }
    case "log_care": {
      const term = asStr(p.plant);
      if (!term) return none(say || 'Which plant? Try "water Monstera".');
      const careRaw = asStr(p.care)?.toLowerCase();
      const care = careRaw && CARE_KINDS_SET.has(careRaw as CareKind) ? (careRaw as CareKind) : undefined;
      if (!care) return none(say || "What kind of care — water, feed, repot, prune, or a note?");
      const rows = await findByName(term);
      if (rows.length === 0) return noMatch(term);
      if (rows.length > 1) return none(`I found ${rows.length} matching "${term}" — be more specific:\n${pickList(rows)}`);
      const pl = rows[0];
      return { reply: say, action: { kind: "log_care", plant_id: pl.id, plant_name: pl.name, care, note: asStr(p.note), done_on: asDate(p.done_on) } };
    }
    case "delete_event": {
      const term = asStr(p.plant);
      if (!term) return none(say || "Which plant's entry do you want to delete?");
      const rows = await findByName(term);
      if (rows.length === 0) return noMatch(term);
      if (rows.length > 1) return none(`I found ${rows.length} matching "${term}" — be more specific:\n${pickList(rows)}`);
      const pl = rows[0];
      // Resolve the entry LOCALLY (the timeline never leaves the device). Narrow by kind and/or
      // date when given; otherwise the most recent. Ambiguity ASKS rather than deleting a guess.
      const careRaw = asStr(p.care)?.toLowerCase();
      const care = careRaw && CARE_KINDS_SET.has(careRaw as CareKind) ? (careRaw as CareKind) : undefined;
      const on = asDate(p.done_on);
      const events = await store.listEvents(pl.id); // newest first
      const matches = events.filter((ev) => (!care || ev.kind === care) && (!on || ev.done_on === on));
      if (matches.length === 0) {
        return none(`I don't see ${care ? `a ${kindLabel(care).toLowerCase()} entry` : "an entry"}${on ? ` on ${on}` : ""} for ${pl.name}.`);
      }
      if (matches.length > 1 && !on && !care) {
        const list = matches.slice(0, 5).map((ev) => `• ${kindLabel(ev.kind)} on ${ev.done_on}`).join("\n");
        return none(`Which one for ${pl.name}?\n${list}`);
      }
      const ev = matches[0];
      return {
        reply: say,
        action: { kind: "delete_event", id: ev.id, label: `the ${kindLabel(ev.kind).toLowerCase()} for ${pl.name} on ${ev.done_on}` },
      };
    }
    case "delete_growth": {
      const term = asStr(p.plant);
      if (!term) return none(say || "Which plant's measurement do you want to delete?");
      const rows = await findByName(term);
      if (rows.length === 0) return noMatch(term);
      if (rows.length > 1) return none(`I found ${rows.length} matching "${term}" — be more specific:\n${pickList(rows)}`);
      const pl = rows[0];
      const on = asDate(p.measured_on);
      const entries = await store.listGrowth(pl.id); // OLDEST first
      const matches = (on ? entries.filter((g) => g.measured_on === on) : entries).slice().reverse(); // newest first
      if (matches.length === 0) return none(`I don't see a measurement${on ? ` on ${on}` : ""} for ${pl.name}.`);
      if (matches.length > 1 && !on) {
        const listed = matches.slice(0, 5).map((g) => `• ${g.measured_on}`).join("\n");
        return none(`Which measurement for ${pl.name}?\n${listed}`);
      }
      const g = matches[0];
      return { reply: say, action: { kind: "delete_growth", id: g.id, label: `${pl.name}'s measurement on ${g.measured_on}` } };
    }
    case "save_to_cloud": {
      const subject = asStr(p.subject)?.toLowerCase() === "conversation" ? "conversation" : "note";
      const content = asStr(p.content);
      if (subject === "note" && !content) return none(say || "What would you like me to save?");
      const providerRaw = asStr(p.provider)?.toLowerCase();
      const provider = providerRaw === "github" ? "github" : (providerRaw === "gdrive" || providerRaw === "drive") ? "gdrive" : undefined;
      const name = asStr(p.name) ?? (subject === "conversation" ? "conversation" : "note");
      return { reply: say, action: { kind: "save_to_cloud", subject, name, content, provider } };
    }
    case "log_growth": {
      const term = asStr(p.plant);
      if (!term) return none(say || 'Which plant? Try "the monstera is 42cm now".');
      const height = asNum(p.height_cm);
      const leaves = asNum(p.leaf_count);
      if (height === undefined && leaves === undefined) {
        return none(say || "Tell me a height in cm or a leaf count and I'll record it.");
      }
      const rows = await findByName(term);
      if (rows.length === 0) return noMatch(term);
      if (rows.length > 1) return none(`I found ${rows.length} matching "${term}" — be more specific:\n${pickList(rows)}`);
      const pl = rows[0];
      return {
        reply: say,
        action: {
          kind: "log_growth", plant_id: pl.id, plant_name: pl.name,
          height_cm: height ?? null,
          leaf_count: leaves === undefined ? null : Math.round(leaves),
          measured_on: asDate(p.measured_on), note: asStr(p.note),
        },
      };
    }
    case "edit_plant": {
      const term = asStr(p.plant);
      if (!term) return none(say || "Which plant do you want to edit? Give me its name.");
      const rows = await findByName(term);
      if (rows.length === 0) return noMatch(term);
      if (rows.length > 1) return none(`I found ${rows.length} matching "${term}" — be more specific:\n${pickList(rows)}`);
      const patch: PlantPatch = {};
      const bits: string[] = [];
      const newName = asStr(p.new_name);
      if (newName) { patch.name = newName; bits.push(`renamed to ${newName}`); }
      const species = asStr(p.species);
      if (species) { patch.species = species; bits.push(`species → ${species}`); }
      const location = asStr(p.location);
      if (location) { patch.location = location; bits.push(`location → ${location}`); }
      const potted_on = asDate(p.potted_on);
      if (potted_on) { patch.potted_on = potted_on; bits.push(`potted ${potted_on}`); }
      if (Object.keys(patch).length === 0) return none(say || "What should I change on that plant?");
      const pl = rows[0];
      return { reply: say, action: { kind: "edit_plant", id: pl.id, patch, label: `${pl.name}${bits.length ? ` (${bits.join(" · ")})` : ""}` } };
    }
    case "delete_plant": {
      const term = asStr(p.plant);
      if (!term) return none(say || "Which plant do you want to delete? Give me its name.");
      const rows = await findByName(term);
      if (rows.length === 0) return noMatch(term);
      if (rows.length > 1) return none(`I found ${rows.length} matching "${term}" — be more specific:\n${pickList(rows)}`);
      return { reply: say, action: { kind: "delete_plant", id: rows[0].id, label: rows[0].name } };
    }
    case "filter": {
      const term = asStr(p.text) ?? "";
      return { reply: "", action: { kind: "filter", term, label: term || "all" } };
    }
    case "recap": {
      const term = asStr(p.plant);
      if (!term) return { reply: say, action: { kind: "recap" } };
      const rows = await findByName(term);
      if (rows.length === 1) return { reply: say, action: { kind: "recap", plant_id: rows[0].id, plant_name: rows[0].name } };
      if (rows.length > 1) return none(`Which one?\n${pickList(rows)}`);
      return noMatch(term);
    }
    case "backup_save":
      return { reply: say, action: { kind: "backup_save" } };
    case "backup_status":
      return { reply: say, action: { kind: "backup_status" } };
    case "backup_restore": {
      const prov = asStr(p.provider);
      return { reply: say, action: { kind: "backup_restore", provider: prov === "github" || prov === "gdrive" ? prov : undefined } };
    }
    case "save_local_copy":
      return { reply: say, action: { kind: "save_local_copy" } };
    case "import_file":
      return { reply: say, action: { kind: "import_file" } };
    case "remember": {
      const instruction = asStr(p.instruction);
      if (!instruction) return none(say || "What should I remember?");
      return { reply: say, action: { kind: "remember", instruction } };
    }
    default:
      return none(say || HELP);
  }
}

export async function routeMessage(text: string): Promise<BrainResponse> {
  const t = text.trim();
  if (!t) return { reply: "", action: { kind: "none", message: HELP } };
  const low = t.toLowerCase();

  // 1) ADD PLANT — "add/create/new [a] plant <name> [species X] [in Y | location Y] [potted YYYY-MM-DD]".
  const addM = t.match(/^(?:add|create|new)\s+(?:an?\s+)?plant\b[:\-\s]*([\s\S]+)$/i);
  if (addM) return { reply: "", action: parseAddPlant(addM[1]) };

  // 2) LOG CARE — a care verb at the start resolves the plant + optional note.
  const care = await routeLogCare(t);
  if (care) return care;

  // 3) EDIT — rename / set species|location|potted / move. Single-match resolution only.
  const edit = await routeEdit(t);
  if (edit) return edit;

  // 4) DELETE — resolve the target locally; the UI asks for confirmation before executing.
  const delM = t.match(/^(?:delete|remove)\s+(?:the\s+|my\s+)?(?:plant\s+)?([\s\S]+)$/i);
  if (delM) {
    const term = clean(delM[1]);
    const rows = await findByName(term);
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: `I couldn't find a plant matching "${term}".` } };
    if (rows.length > 1) return { reply: "", action: { kind: "none", message: `I found ${rows.length} matching "${term}" — be more specific:\n${pickList(rows)}` } };
    return { reply: "", action: { kind: "delete_plant", id: rows[0].id, label: rows[0].name } };
  }

  // 5) RECAP / STORY — the app's AI narration via its EXISTING live endpoints.
  if (/\b(recap|summar(?:y|ise|ize)|overview|this\s+week|how(?:'?s| is| are)\b.*\b(plant|collection|garden|doing)|story|narrate)\b/i.test(low)) {
    const pm = t.match(/(?:story(?:\s+of|\s+for)?|about|narrate|recap)\s+(?:the\s+|my\s+)?([\s\S]+)$/i);
    if (pm) {
      const rows = await findByName(clean(pm[1]));
      if (rows.length === 1) return { reply: "", action: { kind: "recap", plant_id: rows[0].id, plant_name: rows[0].name } };
      if (rows.length > 1) return { reply: "", action: { kind: "none", message: `Which one?\n${pickList(rows)}` } };
    }
    return { reply: "", action: { kind: "recap" } }; // whole-collection "this week"
  }

  // 6) SEARCH / FILTER — query-shaped or mentions "plants" → local search over listPlants.
  if (/^(?:show|find|list|search|which|filter)\b/i.test(t) || /\bplants?\b/i.test(low)) {
    const term = t
      .replace(/^(?:show\s+me|show|find|list|search|which|filter)\s+/i, "")
      .replace(/\b(?:all\s+)?plants?\b/gi, " ")
      .replace(/\b(?:that\s+are|which\s+are|are|is|in|with|for|matching|named|called|the|located)\b/gi, " ")
      .replace(/[.?!]+$/, "")
      .replace(/\s+/g, " ")
      .trim();
    return { reply: "", action: { kind: "filter", term, label: term || "all" } };
  }

  // 7) Unclear → a short clarifying nudge.
  return { reply: "", action: { kind: "none", message: HELP } };
}

// ── ADD PLANT parsing — name is required; species/location/potted are optional keyword fields. ──
function parseAddPlant(rest: string): ChatAction {
  let s = clean(rest);
  let species: string | undefined;
  let location: string | undefined;
  let potted_on: string | null | undefined;

  const pottedM = s.match(/\bpotted(?:\s+on)?\s+(\d{4}-\d{2}-\d{2})/i);
  if (pottedM) { potted_on = pottedM[1]; s = (s.slice(0, pottedM.index) + s.slice(pottedM.index! + pottedM[0].length)).trim(); }

  const speciesM = s.match(/\bspecies\s+(.+?)(?=\s+(?:in|location)\b|[,;]|$)/i);
  if (speciesM) { species = speciesM[1].trim(); s = (s.slice(0, speciesM.index) + s.slice(speciesM.index! + speciesM[0].length)).trim(); }

  const locM = s.match(/\b(?:in|location)\s+(.+?)(?=\s+species\b|[,;]|$)/i);
  if (locM) { location = clean(locM[1].replace(/^the\s+/i, "")); s = (s.slice(0, locM.index) + s.slice(locM.index! + locM[0].length)).trim(); }

  const name = clean(s.replace(/^(?:called|named)\s+/i, "").replace(/[,;]+$/, ""));
  if (!name) return { kind: "none", message: 'What should the plant be called? Try "add plant Monstera".' };
  return { kind: "add_plant", name, species, location, potted_on };
}

// ── LOG CARE. Returns null if the message isn't a care verb so the router can fall through. ──
async function routeLogCare(t: string): Promise<BrainResponse | null> {
  for (const { re, kind } of CARE_VERBS) {
    const m = re.exec(t);
    if (!m) continue;
    let rest = t.slice(m[0].length).trim().replace(/^(?:the|my)\s+/i, "");
    // Optional note after a colon or a SPACE-padded dash (so hyphenated names like "Fiddle-leaf" stay intact).
    let note: string | undefined;
    const noteM = rest.match(/(?:\s*:\s*|\s+[-–—]\s+)(.+)$/);
    if (noteM) { note = noteM[1].trim(); rest = rest.slice(0, noteM.index).trim(); }
    const term = clean(rest);
    if (!term) return { reply: "", action: { kind: "none", message: `Which plant did you ${kind === "note" ? "want to note" : kind}? Try "${kind} Monstera".` } };
    const rows = await findByName(term);
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: `I couldn't find a plant matching "${term}".` } };
    if (rows.length > 1) return { reply: "", action: { kind: "none", message: `I found ${rows.length} matching "${term}" — be more specific:\n${pickList(rows)}` } };
    const p = rows[0];
    return { reply: "", action: { kind: "log_care", plant_id: p.id, plant_name: p.name, care: kind, note } };
  }
  return null;
}

// ── EDIT. Returns null if the message isn't an edit so the router can fall through. ──
async function routeEdit(t: string): Promise<BrainResponse | null> {
  // "rename X to Y"
  let m = t.match(/^rename\s+(.+?)\s+to\s+(.+)$/i);
  if (m) return resolveEdit(m[1], { name: clean(m[2]) }, `renamed to ${clean(m[2])}`);

  // "set/change X('s) species to Y"
  m = t.match(/^(?:set|change)\s+(.+?)(?:'s)?\s+species\s+to\s+(.+)$/i);
  if (m) return resolveEdit(m[1], { species: clean(m[2]) }, `species → ${clean(m[2])}`);

  // "set/change X('s) location to Y"  OR  "move X to [the] Y"
  m = t.match(/^(?:set|change)\s+(.+?)(?:'s)?\s+location\s+to\s+(.+)$/i)
    || t.match(/^move\s+(.+?)\s+to\s+(?:the\s+)?(.+)$/i);
  if (m) return resolveEdit(m[1], { location: clean(m[2]) }, `location → ${clean(m[2])}`);

  // "set/change X('s) potted [date] to YYYY-MM-DD"
  m = t.match(/^(?:set|change)\s+(.+?)(?:'s)?\s+potted(?:\s+date)?\s+to\s+(\d{4}-\d{2}-\d{2})/i);
  if (m) return resolveEdit(m[1], { potted_on: m[2] }, `potted ${m[2]}`);

  return null;
}

async function resolveEdit(term: string, patch: PlantPatch, note: string): Promise<BrainResponse> {
  const rows = await findByName(term);
  if (rows.length === 0) return { reply: "", action: { kind: "none", message: `I couldn't find a plant matching "${clean(term)}".` } };
  if (rows.length > 1) return { reply: "", action: { kind: "none", message: `I found ${rows.length} matching "${clean(term)}" — be more specific:\n${pickList(rows)}` } };
  const p = rows[0];
  return { reply: "", action: { kind: "edit_plant", id: p.id, patch, label: `${p.name} (${note})` } };
}

// Resolve a library title to exactly one item, or explain why not (same 0/1/many
// contract the app uses for its own entries).
function resolveLibraryItem(term: string | undefined): { item?: import("../library").LibraryItem; problem?: string } {
  if (!term) return { problem: "Which saved item do you mean? Give me its title." };
  const found = libraryFind(term);
  if (found.length === 0) return { problem: `I couldn't find "${term}" in your library.` };
  if (found.length > 1) return { problem: `I found ${found.length} matching "${term}" — be more specific: ${found.map((f) => `"${f.title}"`).join(", ")}` };
  return { item: found[0] };
}
